import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, FileText, CheckSquare, TrendingUp, Award, Target, Users, Shield, Zap, Calendar, Star, CheckCircle, BarChart } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export default function HomePage() {
  const heroRef = useRef();
  const whyUsRef = useRef();
  const servicesRef = useRef();
  const processRef = useRef();
  const resultsRef = useRef();
  const proofRef = useRef();
  const ctaRef = useRef();

  // Hero animations
  useGSAP(() => {
    gsap.from('.hero-content > *', {
      opacity: 0,
      y: 30,
      stagger: 0.15,
      duration: 0.8,
      delay: 0.3
    });

    gsap.from('.hero-card', {
      opacity: 0,
      y: 40,
      stagger: 0.1,
      duration: 0.8,
      delay: 0.6
    });

    // Floating animation
    gsap.to('.float-1', { y: -15, duration: 2.5, repeat: -1, yoyo: true, ease: "sine.inOut" });
    gsap.to('.float-2', { y: -20, duration: 3, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 0.5 });
    gsap.to('.float-3', { y: -12, duration: 2.8, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 1 });
  }, { scope: heroRef });

  // Scroll animations
  useGSAP(() => {
    gsap.utils.toArray('.fade-in-section').forEach((section) => {
      gsap.from(section, {
        opacity: 0,
        y: 40,
        duration: 0.8,
        scrollTrigger: {
          trigger: section,
          start: "top 85%",
        }
      });
    });

    gsap.utils.toArray('.stagger-item').forEach((container) => {
      gsap.from(container.children, {
        opacity: 0,
        y: 30,
        stagger: 0.1,
        duration: 0.6,
        scrollTrigger: {
          trigger: container,
          start: "top 80%",
        }
      });
    });
  });

  return (
    <div className="homepage-redesign">
      {/* HERO SECTION - Visually Dominant */}
      <section ref={heroRef} className="hero-redesign">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">Your Partner for Government Contract Success</div>
            <h1 className="hero-headline">
              From Capture Planning to Submission, We Help You Compete with Confidence
            </h1>
            <p className="hero-subtext">
              End-to-end proposal development from capture strategy to final submission. We've helped clients improve their win rates by an average of 30% through strategic capture planning, compelling proposal development, and disciplined review processes.
            </p>
            
            <div className="hero-ctas">
              <Link to="/free-assessment" className="cta-primary">
                <Calendar size={20} />
                Get Free Assessment
              </Link>
              <Link to="/contact" className="cta-secondary">
                Schedule Consultation
              </Link>
            </div>

            <div className="hero-stats-inline">
              <div className="stat-inline">
                <span className="stat-number">200+</span>
                <span className="stat-text">Proposals</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-inline">
                <span className="stat-number">$2.5B+</span>
                <span className="stat-text">Won</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-inline">
                <span className="stat-number">30%</span>
                <span className="stat-text">Win Rate ↑</span>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-card float-1">
              <div className="card-mini-header">
                <CheckSquare size={16} />
                <span>Compliant</span>
              </div>
              <div className="card-mini-title">RFP-2025-1234</div>
              <div className="card-progress-mini">
                <div className="progress-fill" style={{ width: '92%' }}></div>
              </div>
              <div className="card-mini-score">92/100</div>
            </div>

            <div className="hero-card float-2">
              <div className="card-checklist">
                <div className="check-row"><CheckCircle size={14} /> Compliance Matrix</div>
                <div className="check-row"><CheckCircle size={14} /> Win Themes</div>
                <div className="check-row"><CheckCircle size={14} /> Past Performance</div>
                <div className="check-row"><CheckCircle size={14} /> Technical Solution</div>
              </div>
            </div>

            <div className="hero-card float-3">
              <div className="card-stat-large">45%</div>
              <div className="card-stat-label">Win Rate</div>
              <div className="card-stat-trend">
                <TrendingUp size={14} />
                <span>+30% vs baseline</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US SECTION - 4-Card Value Proposition Grid */}
      <section className="why-us-section fade-in-section">
        <div className="section-container">
          <div className="section-intro-compact">
            <h2>Why Government Contractors Choose IronQuill</h2>
            <p>We don't just write proposals—we position you to win before the RFP drops.</p>
          </div>

          <div className="value-prop-grid">
            <div className="value-card">
              <div className="value-icon-wrapper">
                <Target size={32} />
              </div>
              <h3>Win Before the RFP Drops</h3>
              <p>We don't start at submission—we position you early with capture intelligence, competitive analysis, and win strategies that align with agency priorities.</p>
            </div>

            <div className="value-card">
              <div className="value-icon-wrapper">
                <Shield size={32} />
              </div>
              <h3>Built for Government Standards</h3>
              <p>Every proposal is structured to meet strict federal requirements, evaluation criteria, and compliance standards—so nothing gets missed.</p>
            </div>

            <div className="value-card">
              <div className="value-icon-wrapper">
                <FileText size={32} />
              </div>
              <h3>Proposals That Actually Persuade</h3>
              <p>We turn your capabilities into compelling narratives that clearly communicate value, differentiation, and mission alignment.</p>
            </div>

            <div className="value-card">
              <div className="value-icon-wrapper">
                <Zap size={32} />
              </div>
              <h3>From Strategy to Submission</h3>
              <p>From opportunity identification to final submission, we manage the entire lifecycle so your team can stay focused on execution.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION - 2x2 Grid with More Content */}
      <section className="services-dense-section fade-in-section">
        <div className="section-container">
          <div className="section-header-split">
            <div>
              <div className="label-small">What We Do</div>
              <h2>Comprehensive Proposal Services</h2>
            </div>
            <Link to="/services" className="link-arrow">
              View All Services <ArrowRight size={18} />
            </Link>
          </div>

          <div className="services-grid-2x2 stagger-item">
            <div className="service-card-rich">
              <div className="service-number">01</div>
              <FileText size={32} className="service-icon-large" />
              <h3>Proposal Development</h3>
              <p>Transform technical capabilities into compelling, compliant submissions that resonate with evaluators.</p>
              <ul className="service-features">
                <li>Executive summaries</li>
                <li>Technical narratives</li>
                <li>Past performance</li>
              </ul>
              <Link to="/services/proposal-development" className="service-link-inline">
                Learn More →
              </Link>
            </div>

            <div className="service-card-rich">
              <div className="service-number">02</div>
              <Target size={32} className="service-icon-large" />
              <h3>Capture Support</h3>
              <p>Strategic positioning from opportunity identification through RFP release and beyond.</p>
              <ul className="service-features">
                <li>Competitive analysis</li>
                <li>Win theme development</li>
                <li>Teaming strategy</li>
              </ul>
              <Link to="/services/capture-support" className="service-link-inline">
                Learn More →
              </Link>
            </div>

            <div className="service-card-rich">
              <div className="service-number">03</div>
              <Users size={32} className="service-icon-large" />
              <h3>Proposal Management</h3>
              <p>Expert coordination ensuring on-time, compliant submissions under intense deadline pressure.</p>
              <ul className="service-features">
                <li>Team coordination</li>
                <li>Schedule management</li>
                <li>Quality assurance</li>
              </ul>
              <Link to="/services/proposal-management" className="service-link-inline">
                Learn More →
              </Link>
            </div>

            <div className="service-card-rich">
              <div className="service-number">04</div>
              <Award size={32} className="service-icon-large" />
              <h3>Proposal Design</h3>
              <p>Professional visual design that makes proposals stand out while maintaining compliance.</p>
              <ul className="service-features">
                <li>Layout & formatting</li>
                <li>Graphics & charts</li>
                <li>Brand integration</li>
              </ul>
              <Link to="/services/proposal-design" className="service-link-inline">
                Learn More →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION - Compact Horizontal */}
      <section className="process-compact-section fade-in-section">
        <div className="section-container">
          <div className="section-intro-compact centered">
            <div className="label-small">Our Process</div>
            <h2>Capture to Submission in 4 Phases</h2>
          </div>

          <div className="process-flow-horizontal stagger-item">
            <div className="process-step-compact">
              <div className="step-badge">1</div>
              <h4>Capture Planning</h4>
              <p>Positioning before RFP release</p>
            </div>
            <div className="process-arrow">→</div>
            
            <div className="process-step-compact">
              <div className="step-badge">2</div>
              <h4>Development</h4>
              <p>Compelling content creation</p>
            </div>
            <div className="process-arrow">→</div>
            
            <div className="process-step-compact">
              <div className="step-badge">3</div>
              <h4>Review</h4>
              <p>Color team validation</p>
            </div>
            <div className="process-arrow">→</div>
            
            <div className="process-step-compact">
              <div className="step-badge">4</div>
              <h4>Submission</h4>
              <p>Flawless delivery</p>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS SECTION - Split with Stats */}
      <section className="results-split-section fade-in-section">
        <div className="section-container">
          <div className="split-layout">
            <div className="split-content">
              <div className="label-small">Impact</div>
              <h2>Measurable Results That Matter</h2>
              <p className="split-text">
                Our clients don't just submit proposals—they win contracts. With a disciplined approach combining capture strategy, expert writing, and rigorous quality control, we consistently help organizations exceed their win rate targets.
              </p>
              
              <div className="split-highlights">
                <div className="highlight-item">
                  <CheckSquare size={20} />
                  <span>Average 30% win rate improvement</span>
                </div>
                <div className="highlight-item">
                  <CheckSquare size={20} />
                  <span>100% on-time delivery record</span>
                </div>
                <div className="highlight-item">
                  <CheckSquare size={20} />
                  <span>Former government evaluators on staff</span>
                </div>
              </div>

              <Link to="/case-studies" className="link-arrow-large">
                View Case Studies <ArrowRight size={20} />
              </Link>
            </div>

            <div className="split-stats">
              <div className="stat-box">
                <div className="stat-value-large">30%</div>
                <div className="stat-label-large">Win Rate Increase</div>
                <div className="stat-desc">Average across all clients</div>
              </div>

              <div className="stat-box">
                <div className="stat-value-large">$2.5B</div>
                <div className="stat-label-large">Contracts Won</div>
                <div className="stat-desc">Total client success value</div>
              </div>

              <div className="stat-box">
                <div className="stat-value-large">200+</div>
                <div className="stat-label-large">Proposals</div>
                <div className="stat-desc">Successfully delivered</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF SECTION - Testimonials Compact */}
      <section ref={proofRef} className="proof-section fade-in-section">
        <div className="section-container">
          <div className="section-intro-compact centered">
            <div className="label-small">Client Success</div>
            <h2>Trusted by Industry Leaders</h2>
          </div>

          <div className="testimonials-compact stagger-item">
            <div className="testimonial-compact">
              <div className="quote-mark">"</div>
              <p className="quote-text">IronQuill transformed our proposal process. We won our largest contract to date with their strategic support.</p>
              <div className="quote-author">
                <div className="author-name">Sarah Mitchell</div>
                <div className="author-role">VP Business Development, Company name Confidential </div>
              </div>
            </div>

            <div className="testimonial-compact">
              <div className="quote-mark">"</div>
              <p className="quote-text">As a first-time contractor, we won on our first attempt. Their expertise made the impossible possible.</p>
              <div className="quote-author">
                <div className="author-name">Michael Chen</div>
                <div className="author-role">CEO, Company name Confidential</div>
              </div>
            </div>

            <div className="testimonial-compact">
              <div className="quote-mark">"</div>
              <p className="quote-text">Their proposal design gave us a clear competitive edge. Professional quality that stands out.</p>
              <div className="quote-author">
                <div className="author-name">Jennifer Rodriguez</div>
                <div className="author-role">Director of Proposals, Company name Confidential</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION - Compact with Urgency */}
      <section ref={ctaRef} className="cta-final-section fade-in-section">
        <div className="section-container">
          <div className="cta-content-box">
            <div className="cta-left">
              <h2>Need Proposal Support for an Upcoming Opportunity?</h2>
              <p>Get our free Proposal Readiness Checklist and schedule a complimentary consultation to discuss your upcoming RFP.</p>
            </div>
            <div className="cta-right">
              <Link to="/free-assessment" className="cta-primary-large">
                <Calendar size={22} />
                Get Free Assessment
              </Link>
              <Link to="/contact" className="cta-secondary-large">
                Contact Our Team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
